import { LoadingIcon } from '@assets/icons';
import { SearchAndFilter } from '@components/filter';
import { InfiniteScrollTableComponents } from '@components/infiniteScroll';
import { Typography } from '@components/typography';
import { Dataset } from '@generated/graphql';
import { Box, Stack, TableRow, useTheme } from '@mui/material';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import { useCurrentWorkspaceId } from '@navigation/WorkspaceContext';
import { DatasetsActions, useDatasets } from '@subsets/workspaces';
import { TableVirtuoso } from 'react-virtuoso';
import { DatasetsTableHeader } from './DatasetsTableHeader';
import { DatasetsTableRow } from './DatasetsTableRow';

export const DatasetsTable = () => {
  const {
    datasets,
    filter,
    activeFilters,
    loading,
    pagination,
    searchDetail,
    selectedDataset,
    setFilter,
    setSelectedDataset,
  } = useDatasets();

  const { palette } = useTheme();
  const workspaceId = useCurrentWorkspaceId();
  const { organizationId } = useOrganizationContext();

  const handleTableRowSelection = (id: string) => {
    setSelectedDataset(id);
  };

  const tableRow = (props: { item: Dataset }) => (
    <TableRow
      {...props}
      onClick={() => {
        handleTableRowSelection(props.item.datasetId);
      }}
      sx={{
        backgroundColor: props.item.datasetId === selectedDataset && palette.table.row.selected,
      }}
    />
  );

  const item = (_index, dataset: Dataset) => (
    <DatasetsTableRow organizationId={organizationId} workspaceId={workspaceId} dataset={dataset} />
  );

  return (
    <Stack bgcolor={palette.background.paper} height="100%" p={4} pb={0} gap={4}>
      <SearchAndFilter
        context="workspace-datasets"
        filter={filter}
        searchDetail={searchDetail}
        setFilter={(newFilter) => {
          pagination.stopPolling();
          setFilter(newFilter);
        }}
        variant="secondary"
      >
        <DatasetsActions workspaceId={workspaceId} />
      </SearchAndFilter>
      <Stack flex={1}>
        {loading ? (
          <Stack
            bgcolor={palette.background.paper}
            height="100%"
            justifyContent="center"
            alignItems="center"
          >
            <LoadingIcon size={16} />
          </Stack>
        ) : datasets.length === 0 ? (
          <Stack
            bgcolor={palette.background.paper}
            height="100%"
            justifyContent="center"
            alignItems="center"
          >
            <Typography color={palette.text.paper}>
              {activeFilters
                ? 'No datasets found matching your search criteria.'
                : 'No datasets exist in this workspace.'}
            </Typography>
          </Stack>
        ) : (
          <Box data-test-id="virtuoso-item-list" sx={{ flex: 1, height: 0 }}>
            <TableVirtuoso
              data={datasets}
              endReached={pagination.fetchMore}
              components={{
                ...InfiniteScrollTableComponents,
                TableRow: tableRow,
              }}
              fixedHeaderContent={DatasetsTableHeader}
              itemContent={item}
              increaseViewportBy={400}
            />
          </Box>
        )}
      </Stack>
    </Stack>
  );
};
